import {
  NavReconClientAuditStatus,
  NavReconPipelineMetadata,
  NavReconPipelineSnapshot,
  NavReconPipelineStage,
  NavReconRiskMetadataStatus,
} from '../../../../api/rs-hg/nav-recon/types';

export const NAV_RECON_RISK_MONITOR = 'nav_reconciliation_risk_monitor';
export const NAV_RECON_NAV_DATA = 'nav_reconciliation';

/** Ordered stages the NAV recon batch runs through before overview rows are complete. */
export const NAV_RECON_PIPELINE_STAGE_ORDER = [
  'position',
  'nav_reconciliation',
  'nav_reconciliation_risk_monitor',
] as const;

export type NavReconPipelineStageKey =
  (typeof NAV_RECON_PIPELINE_STAGE_ORDER)[number];

const STAGE_DISPLAY_LABEL: Record<NavReconPipelineStageKey, string> = {
  position: 'Waiting for positions to finish.',
  nav_reconciliation: 'Waiting for NAV reconciliation to finish.',
  nav_reconciliation_risk_monitor:
    'Waiting for NAV reconciliation risk monitor to finish.',
};

const STAGE_SHORT_LABEL: Record<NavReconPipelineStageKey, string> = {
  position: 'Positions',
  nav_reconciliation: 'NAV recon',
  nav_reconciliation_risk_monitor: 'Risk monitor',
};

const findStageInsensitive = (
  stages: NavReconPipelineStage[],
  name: NavReconPipelineStageKey,
): NavReconPipelineStage | undefined =>
  stages.find((s) => s.rs_data_name?.toLowerCase() === name.toLowerCase());

export type NavReconFundPipelineProgress =
  | { status: 'ready' }
  | {
      status: 'pending';
      waitingOnStage: NavReconPipelineStageKey;
      displayLabel: string;
      shortLabel: string;
    };

export const getNavReconFundPipelineProgress = (
  fundMeta: NavReconPipelineMetadata | undefined,
): NavReconFundPipelineProgress => {
  if (!fundMeta?.stages?.length) {
    return {
      status: 'pending',
      waitingOnStage: 'position',
      displayLabel:
        'Pipeline has not reported any stages for this fund yet. Values may still be calculating.',
      shortLabel: 'Pending',
    };
  }

  for (const stageKey of NAV_RECON_PIPELINE_STAGE_ORDER) {
    const stage = findStageInsensitive(fundMeta.stages, stageKey);
    const snapshots = stage?.snapshots ?? [];
    if (snapshots.length === 0) {
      return {
        status: 'pending',
        waitingOnStage: stageKey,
        displayLabel: STAGE_DISPLAY_LABEL[stageKey],
        shortLabel: STAGE_SHORT_LABEL[stageKey],
      };
    }
  }

  return { status: 'ready' };
};

export const buildPipelineProgressByFund = (
  metadata: NavReconPipelineMetadata[],
): Record<string, NavReconFundPipelineProgress> =>
  metadata.reduce<Record<string, NavReconFundPipelineProgress>>((acc, fund) => {
    acc[fund.fund_name] = getNavReconFundPipelineProgress(fund);
    return acc;
  }, {});

export const getNavReconFundPipelineProgressForFundName = (
  fundName: string,
  progressByFund: Record<string, NavReconFundPipelineProgress>,
): NavReconFundPipelineProgress =>
  progressByFund[fundName] ?? {
    status: 'pending',
    waitingOnStage: 'position',
    displayLabel:
      'No pipeline metadata for this fund yet. Values may still be calculating.',
    shortLabel: 'Pending',
  };

/** Longer labels for the per-row pipeline details menu. */
export const NAV_RECON_PIPELINE_STAGE_MENU_TITLES: Record<
  NavReconPipelineStageKey,
  string
> = {
  position: 'Positions Data',
  nav_reconciliation: 'NAV Data',
  nav_reconciliation_risk_monitor: 'NAV Reconciliation Calculation',
};

export type NavReconPipelineStageMenuItemState =
  | 'succeeded'
  | 'error'
  | 'loading'
  | 'current'
  | 'upcoming';

export type NavReconPipelineStageMenuItem = {
  stageKey: NavReconPipelineStageKey;
  title: string;
  state: NavReconPipelineStageMenuItemState;
  snapshotCount: number;
  latestSnapshotLabel: string | null;
  latestSnapshotStatus: NavReconRiskMetadataStatus | null;
  snapshots: NavReconPipelineSnapshot[];
};

export interface NavReconAuditMetadata {
  clientAuditStatus: NavReconClientAuditStatus;
  snapshotTimestamp: string | null;
  clientComment: string | null;
  clientAuditByName: string | null;
}

export const getLatestSnapshot = (
  snapshots: NavReconPipelineSnapshot[],
): NavReconPipelineSnapshot | undefined =>
  snapshots.reduce<NavReconPipelineSnapshot | undefined>((latest, snapshot) => {
    if (!latest) return snapshot;
    return snapshot.snapshot_timestamp > latest.snapshot_timestamp
      ? snapshot
      : latest;
  }, undefined);

const normalizeSnapshotStatus = (
  status: string | null | undefined,
): NavReconRiskMetadataStatus | null => {
  if (!status) return null;
  const normalized = status.toLowerCase();
  if (
    normalized === 'loading' ||
    normalized === 'system_error' ||
    normalized === 'succeeded'
  ) {
    return normalized;
  }
  return null;
};

/** Latest snapshot outcome — older snapshots are ignored once a newer run exists. */
export const getStageSnapshotOutcome = (
  snapshots: NavReconPipelineSnapshot[],
): NavReconRiskMetadataStatus | 'none' => {
  if (!snapshots.length) return 'none';

  const latest = getLatestSnapshot(snapshots);
  const status = normalizeSnapshotStatus(latest?.status);
  return status ?? 'succeeded';
};

export const isNavReconPipelineStageSucceeded = (
  snapshots: NavReconPipelineSnapshot[] | undefined,
): boolean => getStageSnapshotOutcome(snapshots ?? []) === 'succeeded';

/** One row per ordered stage — for a compact “burger” details menu. */
export const getPipelineStageMenuItems = (
  fundMeta: NavReconPipelineMetadata | undefined,
): NavReconPipelineStageMenuItem[] => {
  const stages = fundMeta?.stages ?? [];
  let seenIncomplete = false;

  return NAV_RECON_PIPELINE_STAGE_ORDER.map((stageKey) => {
    const stage = stages.length
      ? findStageInsensitive(stages, stageKey)
      : undefined;
    const snapshots = stage?.snapshots ?? [];
    const count = snapshots.length;
    const outcome = getStageSnapshotOutcome(snapshots);

    let state: NavReconPipelineStageMenuItemState;
    if (outcome === 'system_error') {
      state = 'error';
    } else if (outcome === 'loading') {
      state = 'loading';
    } else if (outcome === 'succeeded') {
      state = 'succeeded';
    } else if (!seenIncomplete) {
      state = 'current';
      seenIncomplete = true;
    } else {
      state = 'upcoming';
    }

    const latest = count > 0 ? getLatestSnapshot(snapshots) : undefined;
    let latestSnapshotLabel: string | null = null;
    if (latest) {
      const parsed = new Date(latest.snapshot_timestamp);
      latestSnapshotLabel = Number.isNaN(parsed.getTime())
        ? latest.snapshot_timestamp
        : parsed.toLocaleString();
    }

    return {
      stageKey,
      title: NAV_RECON_PIPELINE_STAGE_MENU_TITLES[stageKey],
      state,
      snapshotCount: count,
      latestSnapshotLabel,
      latestSnapshotStatus: outcome === 'none' ? null : outcome,
      snapshots: [...snapshots].sort((a, b) =>
        b.snapshot_timestamp.localeCompare(a.snapshot_timestamp),
      ),
    };
  });
};

export type NavReconPipelineAggregateStatus =
  | 'error'
  | 'complete'
  | 'in_progress'
  | 'not_started';

export const NAV_RECON_PIPELINE_AGGREGATE_STATUS_LABEL: Record<
  NavReconPipelineAggregateStatus,
  string
> = {
  error: 'Error',
  complete: 'Complete',
  in_progress: 'In progress',
  not_started: 'Not started',
};

/** Overall pipeline status from each stage's latest snapshot only. */
export const getNavReconPipelineAggregateStatus = (
  fundMeta: NavReconPipelineMetadata | undefined,
): NavReconPipelineAggregateStatus => {
  const items = getPipelineStageMenuItems(fundMeta);
  if (items.some((item) => item.state === 'error')) {
    return 'error';
  }
  if (items.length > 0 && items.every((item) => item.state === 'succeeded')) {
    return 'complete';
  }
  if (items.every((item) => item.state === 'upcoming')) {
    return 'not_started';
  }
  return 'in_progress';
};

export type NavReconPipelineButtonStatus = 'error' | 'complete' | 'default';

/** Compact status for the per-row pipeline trigger button. */
export const getNavReconPipelineButtonStatus = (
  fundMeta: NavReconPipelineMetadata | undefined,
): NavReconPipelineButtonStatus => {
  const aggregateStatus = getNavReconPipelineAggregateStatus(fundMeta);
  if (aggregateStatus === 'error') return 'error';
  if (aggregateStatus === 'complete') return 'complete';
  return 'default';
};

export const buildFundPipelineMetadataByFundName = (
  metadata: NavReconPipelineMetadata[],
): Record<string, NavReconPipelineMetadata | undefined> =>
  metadata.reduce<Record<string, NavReconPipelineMetadata | undefined>>(
    (acc, fund) => {
      acc[fund.fund_name] = fund;
      return acc;
    },
    {},
  );

export const buildAuditMetadataByFund = (
  metadata: NavReconPipelineMetadata[],
): Record<string, NavReconAuditMetadata> =>
  metadata.reduce<Record<string, NavReconAuditMetadata>>((acc, fund) => {
    const riskMonitorStage = fund.stages.find(
      (stage) => stage.rs_data_name === NAV_RECON_RISK_MONITOR,
    );
    const latestSnapshot = getLatestSnapshot(riskMonitorStage?.snapshots ?? []);

    acc[fund.fund_name] = {
      clientAuditStatus: latestSnapshot?.client_audit_status ?? null,
      snapshotTimestamp: latestSnapshot?.snapshot_timestamp ?? null,
      clientComment: latestSnapshot?.client_comment ?? null,
      clientAuditByName: latestSnapshot?.client_audit_by_name ?? null,
    };
    return acc;
  }, {});

/**
 * Timestamp when "NAV Reconciliation Calculation" (`nav_reconciliation_risk_monitor`)
 * completed successfully — starts the 2h NAV timer. Null while still running or on error.
 */
export const getNavReconCalculationCompleteTimestamp = (
  fundMeta: NavReconPipelineMetadata | undefined,
): string | null => {
  if (!fundMeta?.stages?.length) return null;

  const calcStage = findStageInsensitive(
    fundMeta.stages,
    NAV_RECON_RISK_MONITOR,
  );
  const snapshots = calcStage?.snapshots ?? [];
  if (getStageSnapshotOutcome(snapshots) !== 'succeeded') return null;

  return getLatestSnapshot(snapshots)?.snapshot_timestamp ?? null;
};

/** Per-fund timer start times (calculation complete). */
export const buildNavReceivedTimestampByFund = (
  metadata: NavReconPipelineMetadata[],
): Record<string, string | null> =>
  metadata.reduce<Record<string, string | null>>((acc, fund) => {
    acc[fund.fund_name] = getNavReconCalculationCompleteTimestamp(fund);
    return acc;
  }, {});

const getFundClientAuditStatus = (
  fundMeta: NavReconPipelineMetadata,
): string | null => {
  const riskMonitorStage = findStageInsensitive(
    fundMeta.stages,
    NAV_RECON_RISK_MONITOR,
  );
  const latestSnapshot = getLatestSnapshot(riskMonitorStage?.snapshots ?? []);
  return latestSnapshot?.client_audit_status ?? null;
};

/**
 * True when at least one fund has a completed NAV Reconciliation Calculation
 * whose snapshot date matches the requested position date and is not yet
 * approved — drives the NAV-Recon top-nav badge / toast.
 */
export const hasNavReconIncomingNavs = (
  metadata: NavReconPipelineMetadata[] | undefined,
  positionDate: string | undefined,
): boolean => {
  if (!metadata?.length || !positionDate) return false;

  const matchingFunds = metadata
    .map((fund) => ({
      fund: fund.fund_name,
      calcCompleteAt: getNavReconCalculationCompleteTimestamp(fund),
      auditStatus: getFundClientAuditStatus(fund),
    }))
    .filter(
      ({ calcCompleteAt, auditStatus }) =>
        Boolean(calcCompleteAt) &&
        calcCompleteAt!.startsWith(positionDate) &&
        String(auditStatus ?? '').toLowerCase() !== 'approved',
    );

  return matchingFunds.length > 0;
};
